// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { PerformanceLineChart } from '@shared/ui-kit/features/performance-line-chart';
import {
  KpiBoxes,
  ReturnsChart,
  type KpiBoxesProps,
} from '@shared/ui-kit/components';
import { useMemo, type FC } from 'react';
import { type PortfolioBacktest } from '@shared/data-access/interfaces/api/ai/portfolio';
import { type BaseMultiAssetPortfolioAnalysis } from '@shared/data-access/interfaces/api/ai/portfolio-analysis';
import { useTranslation } from 'react-i18next';

export interface MultiAssetProps {
  analysis?: BaseMultiAssetPortfolioAnalysis;

  backtest?: PortfolioBacktest;

  returns: {
    label: string;
    portfolio: number;
    benchmark: number;
  }[];

  kpis: KpiBoxesProps[];

  feature: 'aggregator' | 'rebalance';
}

export const MultiAsset: FC<MultiAssetProps> = ({
  kpis,
  returns,
  analysis,
  backtest,
  feature,
}) => {
  const { t } = useTranslation();
  const hasUnsupportedTickers = useMemo(
    () =>
      analysis &&
      (analysis?.unsupported_benchmarks.length > 0 ||
        analysis?.unsupported_ticker_positions.length > 0),
    [analysis],
  );

  const unsupportedTickers = useMemo(
    () =>
      Array.from(
        new Set(
          analysis?.unsupported_benchmarks.concat(
            analysis?.unsupported_ticker_positions,
          ),
        ),
      ).join(', '),
    [analysis],
  );

  return (
    <>
      {analysis && hasUnsupportedTickers && (
        <p
          className="max-w-[30%] text-base text-negative"
          data-testid="unsupported-tickers"
        >
          Positions not included:&nbsp;
          {unsupportedTickers}
        </p>
      )}
      <div className="grid grid-cols-4 gap-6">
        {kpis.map((vals, idx) => (
          <KpiBoxes key={idx} feature={feature} {...vals} />
        ))}
      </div>
      {backtest && (
        <>
          <PerformanceLineChart
            data={backtest!.ts_backtest}
            isReturns={true}
            name={t('backtest.returns', { ns: 'components' })}
            feature={feature}
          />
          <PerformanceLineChart
            data={backtest!.ts_drawdowns}
            isReturns={false}
            name={t('backtest.drawdowns', { ns: 'components' })}
            crossType="top"
            feature={feature}
          />
        </>
      )}
      <ReturnsChart data={returns} feature={feature} />
    </>
  );
};
